/**
 * @file src/helpers/analyticsEvents.ts
 * @exports ANALYTICS, AnalyticsAuthFailReason, AnalyticsBeamExtractionFailReason, AnalyticsCameraFailReason, AnalyticsCameraResumeResult, AnalyticsCaptchaFlow, AnalyticsCaptureTrigger, AnalyticsConnectionState, AnalyticsDisputeFailReason, AnalyticsIdCountry, AnalyticsInviteFailReason, AnalyticsLivenessCameraAbandonReason, AnalyticsLivenessCameraStage, AnalyticsLivenessCoachingReason, AnalyticsLivenessCoachingState, AnalyticsOrderFailReason, AnalyticsRequestFailReason, AnalyticsScanPath, AnalyticsSide, AnalyticsSignatureMethod, AnalyticsStatus, AnalyticsSurface, AnalyticsTwoFactorAction, AnalyticsUploadRejectReason, AnalyticsUserErrorReason, AnalyticsVoucherRejectReason
 * @summary Normalized analytics event names and property values for Cognition and vID Web.
 * @remarks Upload rejection reasons distinguish unsupported formats, altered extensions, and file-read errors.
 *          User error reasons enumerate each vID error notification, including capture continuation failures
 *          and the full-page fallbacks for a route whose build chunk is missing or whose render threw.
 *          Failure reasons are bounded per flow and shared with the vID App; error detail goes to Sentry, not here.
 *          Camera resume results report whether preview frames came back after a return, not confirmed white screens.
 *          `abandoned` is an outcome of its own: the user left before the flow succeeded or failed.
 */

/** Outcome of a flow that can fail, carried by every collapsed event name. */
export type AnalyticsStatus = "abandoned" | "attempted" | "succeeded" | "failed";

/** Which face of an ID document an event refers to. */
export type AnalyticsSide = "back" | "front";

/** What caused remote (phone) capture to be initialized. */
export type AnalyticsCaptureTrigger = "after_failed_scan" | "immediate";

/** Whether a desktop scan ran locally or was handed off to a phone. */
export type AnalyticsScanPath = "local" | "remote";

/** Lifecycle of the desktop-to-phone WebRTC connection. */
export type AnalyticsConnectionState = "established" | "ended" | "failed";

/**
 * ID categories that name an actual country of issue. The picker also offers
 * NO_VALID_ID and MANUAL_ENTRY, which are not countries and are reported via id_class.
 */
export type AnalyticsIdCountry = "INTERNATIONAL" | "US";

/**
 * How the camera-preview check ended after the user returned to vID Web during ID capture.
 * A timeout is a camera-resume failure, not a confirmed white screen.
 */
export type AnalyticsCameraResumeResult = "frames_resumed" | "observation_interrupted" | "resume_timed_out";

/** Which form a Cloudflare Turnstile check failed on. */
export type AnalyticsCaptchaFlow = "recovery" | "signup";

/** How the user moved through two-factor enrolment. */
export type AnalyticsTwoFactorAction = "advanced" | "skipped" | "toggled";

/** Which surface the user chose from the vID landing page. */
export type AnalyticsSurface = "app" | "web";

/** How the user chose to sign a disclosure. */
export type AnalyticsSignatureMethod = "draw" | "type";

/** Why Beam document extraction produced no usable data. */
export type AnalyticsBeamExtractionFailReason = "no_data" | "request_failed";

/** Failure reasons any request-backed flow can report when nothing more specific is known. */
export type AnalyticsRequestFailReason = "network_error" | "server_error" | "unknown";

/** Why an account, verification, recovery, or API-key unlock step failed. */
export type AnalyticsAuthFailReason =
	| AnalyticsRequestFailReason
	| "code_already_used"
	| "delivery_failed"
	| "flow_expired"
	| "flow_rejected"
	| "invalid_code"
	| "missing_code"
	| "too_many_attempts";

/** Why accepting a vID invite failed. */
export type AnalyticsInviteFailReason = AnalyticsRequestFailReason | "already_accepted" | "voucher_invalid";

/** Why submitting a vID order or Beam form failed. */
export type AnalyticsOrderFailReason =
	| AnalyticsRequestFailReason
	| "missing_order_id"
	| "upload_failed"
	| "validation_failed";

/** Why submitting a dispute failed. */
export type AnalyticsDisputeFailReason = "network_error" | "oversized_upload" | "unknown" | "validation_failed";

/** Camera initialization failures shared with the App's Vision Camera error codes. */
export type AnalyticsCameraFailReason =
	| "device/camera-already-in-use"
	| "device/no-device"
	| "parameter/invalid-parameter"
	| "permission/camera-permission-denied"
	| "session/camera-not-ready"
	| "system/camera-is-restricted"
	| "unknown/unknown";

/** How the user left a liveness camera startup that had not yet succeeded or failed. */
export type AnalyticsLivenessCameraAbandonReason = "camera_closed" | "page_exit" | "screen_left";

/** The step a liveness camera startup had reached, in order. */
export type AnalyticsLivenessCameraStage =
	| "permission_check"
	| "get_user_media"
	| "apply_constraints"
	| "load_metadata"
	| "play";

/** Why an uploaded ID image was rejected before scanning. */
export type AnalyticsUploadRejectReason = "altered_file" | "unsupported_type" | "read_error";

/** Which vID error notification or dialog was shown to the user. */
export type AnalyticsUserErrorReason =
	| "api_key_unlock_code_send_failed"
	| "beam_extraction_failed"
	| "beam_submit_failed"
	| "biometric_consent_context_missing"
	| "biometric_consent_record_failed"
	| "biometric_consent_unavailable"
	| "biometric_opt_out_failed"
	| "disclosure_unsigned"
	| "feedback_submit_failed"
	| "id_capture_failed"
	| "id_image_processing_failed"
	| "id_scan_failed"
	| "id_upload_invalid"
	| "invalid_invite_url"
	| "invite_accept_failed"
	| "invite_param_invalid"
	| "liveness_failed"
	| "order_create_failed"
	| "page_render_failed"
	| "qr_code_generate_failed"
	| "remote_capture_init_failed"
	| "route_load_failed"
	| "sample_id_upload_failed"
	| "selfie_missing"
	| "selfie_not_found"
	| "selfie_upload_failed"
	| "storage_full"
	| "voucher_check_failed"
	| "voucher_not_found"
	| "voucher_rejected";

/** Why a voucher could no longer start the vID flow; the user is shown the expired page. */
export type AnalyticsVoucherRejectReason = "canceled" | "expired" | "redeemed";

/**
 * Coaching feedback surfaced during the liveness challenge. Shares `reset` and
 * `countdown_restarted` with the vID App; `step_advanced` reports the server's
 * next instruction, since Web's challenge steps are server-driven.
 */
export type AnalyticsLivenessCoachingState = "countdown_restarted" | "reset" | "step_advanced";

/** Why the liveness challenge or its selfie countdown was interrupted. */
export type AnalyticsLivenessCoachingReason = "face_missing" | "server_retake" | "socket_disconnected";

/** Canonical Mixpanel event names. */
export const ANALYTICS = {
	accountCreated: "Account Created",
	aliasAddressAdded: "Alias Address Added",
	aliasNameAdded: "Alias Name Added",
	apiKeyUnlockCodeResent: "API Key Unlock Code Resent",
	apiKeyUnlockCodeSent: "API Key Unlock Code Sent",
	apiKeyUnlocked: "API Key Unlocked",
	beamAdvanced: "Beam Advanced",
	beamDataExtracted: "Beam Data Extracted",
	beamSubmitted: "Beam Submitted",
	biometricConsentAccepted: "Biometric Consent Accepted",
	biometricConsentDeclined: "Biometric Consent Declined",
	captchaFailed: "Captcha Failed",
	desktopScanCompleted: "Desktop Scan Completed",
	disclosureSignatureMethodSelected: "Disclosure Signature Method Selected",
	disclosureSigned: "Disclosure Signed",
	disputeSubmitted: "Dispute Submitted",
	emailVerified: "Email Verified",
	emailVerifyCodeResent: "Email Verify Code Resent",
	idAutoCaptured: "ID Auto Captured",
	idCameraResumeChecked: "ID Camera Resume Checked",
	idCaptureProgressed: "ID Capture Progressed",
	idCountrySelected: "ID Country Selected",
	idInstructionsViewed: "ID Instructions Viewed",
	idRetakeStarted: "ID Retake Started",
	idScanRetried: "ID Scan Retried",
	idScanned: "ID Scanned",
	idTypeSelected: "ID Type Selected",
	idUploadRejected: "ID Upload Rejected",
	inviteAccepted: "Invite Accepted",
	livenessCameraInitialized: "Liveness Camera Initialized",
	livenessCoachingShown: "Liveness Coaching Shown",
	livenessCompleted: "Liveness Completed",
	livenessFrameProcessorToggled: "Liveness Frame Processor Toggled",
	livenessStarted: "Liveness Started",
	loginCodeVerified: "Login Code Verified",
	manualEntryStarted: "Manual Entry Started",
	nextStepRouted: "Next Step Routed",
	noValidIdSelected: "No Valid ID Selected",
	orderSubmitted: "Order Submitted",
	recoveryCodeResent: "Recovery Code Resent",
	recoveryLinkSent: "Recovery Link Sent",
	redirectWaitStarted: "Redirect Wait Started",
	remoteCaptureCameraInitialized: "Remote Capture Camera Initialized",
	remoteCaptureConnectionChanged: "Remote Capture Connection Changed",
	remoteCaptureInitialized: "Remote Capture Initialized",
	remoteCaptureQrFollowed: "Remote Capture QR Followed",
	remoteCaptureQrGenerated: "Remote Capture QR Generated",
	reviewInfoCompleted: "Review Info Completed",
	screenViewed: "Screen Viewed",
	selfieAccepted: "Selfie Accepted",
	selfieCaptured: "Selfie Captured",
	selfieRetaken: "Selfie Retaken",
	ssnSubmitted: "SSN Submitted",
	surfaceChosen: "Surface Chosen",
	twoFactorSetupChanged: "Two Factor Setup Changed",
	userErrorShown: "User Error Shown",
	voucherOpened: "Voucher Opened",
	voucherRejected: "Voucher Rejected",
	websiteTranslated: "Website Translated",
} as const;
